import {z} from 'zod'

import {type Classroom, classroomLink} from './types'

/**
 * A classroom's card, as an invitation link carries it.
 *
 * The room lives on the server, so the card only names it - its id, the
 * owner's handle and the room's handle (the address the server finds it by) -
 * gives it a name to show while it loads, and carries the join code. The code
 * is the one secret here, and it rides on purpose: a link the school hands out
 * is an invitation, and an invitation is a code somebody else now knows.
 *
 * Cards written by earlier builds carried more (level, country, visibility).
 * Those links are still in people's messages, so they still read: the extra
 * fields are ignored and their `joinCode` is read as `code`.
 */
export const classroomInviteSchema = z.object({
  /** The card's shape. A card written by a newer app is read as no card. */
  v: z.literal(1),
  id: z.string().min(1),
  ownerHandle: z.string().min(1).max(253),
  handle: z.string().min(1).max(60),
  name: z.string().max(80),
  code: z.string().max(20).optional(),
})
export type ClassroomInvite = z.infer<typeof classroomInviteSchema>

/** The card a room hands out for itself. */
export function makeClassroomInvite(classroom: Classroom): ClassroomInvite {
  return {
    v: 1,
    id: classroom.id,
    ownerHandle: classroom.ownerHandle,
    handle: classroom.handle,
    name: classroom.name.slice(0, 80),
    code: classroom.joinCode?.slice(0, 20) || undefined,
  }
}

/**
 * The link a school hands out: the room's address, with the card on it when
 * there is a code to carry.
 *
 * `joinCode` is only on a room for a viewer the server lets invite, so the
 * code rides exactly when the person sharing could have read it out anyway. A
 * room with no code (or a viewer who was not given it) shares its plain
 * address, which is shorter and a crisper QR code.
 */
export function classroomInvitationLink(classroom: Classroom): string {
  if (!classroom.joinCode) return classroomLink(classroom)
  return `${classroomLink(classroom)}?invite=${encodeClassroomInvite(
    makeClassroomInvite(classroom),
  )}`
}

const BASE64URL =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

const REVERSE = new Map(
  [...BASE64URL].map((character, index) => [character, index]),
)

/** A string's UTF-8 bytes, written out rather than borrowed from a runtime. */
function utf8Bytes(text: string): number[] {
  const bytes: number[] = []
  for (const character of text) {
    const code = character.codePointAt(0)!
    if (code < 0x80) {
      bytes.push(code)
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f))
    } else if (code < 0x10000) {
      bytes.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      )
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      )
    }
  }
  return bytes
}

/** Bytes, read back as the string they were written from. */
function utf8String(bytes: number[]): string {
  let out = ''
  for (let i = 0; i < bytes.length;) {
    const first = bytes[i]
    let code: number
    let width: number
    if (first < 0x80) {
      code = first
      width = 1
    } else if (first < 0xe0) {
      code = first & 0x1f
      width = 2
    } else if (first < 0xf0) {
      code = first & 0x0f
      width = 3
    } else {
      code = first & 0x07
      width = 4
    }
    if (i + width > bytes.length) break
    for (let k = 1; k < width; k++) {
      code = (code << 6) | (bytes[i + k] & 0x3f)
    }
    out += String.fromCodePoint(code)
    i += width
  }
  return out
}

/*
 * The card as this build writes it: values in a fixed order, no names, so a
 * QR code has as little as possible to draw. The first value is the shape.
 */
const CARD_VERSION = 4
type CompactV4 = [
  version: typeof CARD_VERSION,
  id: string,
  name: string,
  handle: string,
  ownerHandle: string,
  code: string | null,
]

function toCompact(invite: ClassroomInvite): CompactV4 {
  return [
    CARD_VERSION,
    invite.id,
    invite.name,
    invite.handle,
    invite.ownerHandle,
    invite.code || null,
  ]
}

/** The fields every card shape agrees on, ready for the schema. */
function card(fields: {
  id: unknown
  name: unknown
  handle: unknown
  ownerHandle: unknown
  code: unknown
}): unknown {
  return {
    v: 1,
    id: fields.id,
    name: fields.name,
    handle: fields.handle,
    ownerHandle: fields.ownerHandle,
    // Absent and empty both mean no code
    code:
      typeof fields.code === 'string' && fields.code ? fields.code : undefined,
  }
}

/*
 * Tuples written by earlier builds. v2 and v3 put the owner's handle at index
 * 7 and the code at 8; whatever follows (year, country, ...) is ignored.
 */
function fromTuple(value: unknown[]): unknown {
  if (value[0] === CARD_VERSION) {
    const [, id, name, handle, ownerHandle, code] = value
    return card({id, name, handle, ownerHandle, code})
  }
  if (value[0] === 2 || value[0] === 3) {
    const [, id, name, handle, , , , ownerHandle, code] = value
    return card({id, name, handle, ownerHandle, code})
  }
  return undefined
}

/** Object cards: the one-letter keyed card, or the original long one. */
function fromObject(value: unknown): unknown {
  if (!value || typeof value !== 'object') return undefined
  const o = value as Record<string, unknown>
  if (o.v !== 1) return undefined
  if (o.id === undefined) {
    return card({id: o.i, name: o.n, handle: o.h, ownerHandle: o.oh, code: o.c})
  }
  return card({
    id: o.id,
    name: o.name,
    handle: o.handle,
    ownerHandle: o.ownerHandle,
    code: o.code ?? o.joinCode,
  })
}

/**
 * The card, as the text a link carries.
 *
 * base64url of the card's UTF-8 JSON, not percent-encoded JSON: a name written
 * in Arabic is two bytes per letter in UTF-8 and *six* percent-encoded
 * (`%D8%A7`), so a card a school has to put in a QR code — or paste into a
 * message — comes out at a third of the size this way, and one that a camera can
 * still read. The alphabet is URL-safe, so the text needs no escaping of its own.
 */
export function encodeClassroomInvite(invite: ClassroomInvite): string {
  const bytes = utf8Bytes(JSON.stringify(toCompact(invite)))
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]
    const b = i + 1 < bytes.length ? bytes[i + 1] : undefined
    const c = i + 2 < bytes.length ? bytes[i + 2] : undefined
    out += BASE64URL[a >> 2]
    out += BASE64URL[((a & 0x03) << 4) | (b === undefined ? 0 : b >> 4)]
    if (b === undefined) break
    out += BASE64URL[((b & 0x0f) << 2) | (c === undefined ? 0 : c >> 6)]
    if (c === undefined) break
    out += BASE64URL[c & 0x3f]
  }
  return out
}

/** The card out of that text, or nothing when it is not one. */
function decodeClassroomInviteText(value: string): unknown {
  const bytes: number[] = []
  let buffer = 0
  let bits = 0
  for (const character of value.trim()) {
    const six = REVERSE.get(character)
    if (six === undefined) return undefined
    buffer = (buffer << 6) | six
    bits += 6
    if (bits >= 8) {
      bits -= 8
      bytes.push((buffer >> bits) & 0xff)
    }
  }
  try {
    return JSON.parse(utf8String(bytes))
  } catch {
    return undefined
  }
}

/**
 * The first card was percent-encoded JSON. Links that carried one are still in
 * somebody's messages.
 */
function decodeLegacyInviteText(value: string): unknown {
  try {
    return JSON.parse(decodeURIComponent(value))
  } catch {
    return undefined
  }
}

/**
 * The card a link carries, or nothing. A link that was truncated, edited or
 * written by another version reads as no card rather than half a room.
 */
export function decodeClassroomInvite(
  value: string | undefined,
): ClassroomInvite | null {
  if (!value) return null
  const compact = decodeClassroomInviteText(value)
  const parsed =
    compact === undefined
      ? fromObject(decodeLegacyInviteText(value))
      : Array.isArray(compact)
        ? fromTuple(compact)
        : fromObject(compact)
  const result = classroomInviteSchema.safeParse(parsed)
  return result.success ? result.data : null
}

/**
 * The card in whatever somebody pasted: an invitation link, or the card text
 * on its own. A bare code is not a card - the join dialog looks those up on
 * the server.
 */
export function readClassroomInviteFromText(
  text: string,
): ClassroomInvite | null {
  const trimmed = text.trim()
  const inLink = /[?&]invite=([^&\s#]+)/.exec(trimmed)
  return decodeClassroomInvite(inLink ? inLink[1] : trimmed)
}
